/* eslint-disable yoda */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import { BlockDayComp } from './calendar-main-page/block-day-comp';
import { SpaceIsAvailableComp } from './calendar-main-page/space-is-available-comp';
import { BookingInfoComp } from './calendar-main-page/booking-info-comp';
import { SpaceSelector } from './shared-component/space-selector';

import { Calendar, CalendarAvailabilityDayContent, CalendarEventLegend } from '@ps-design-system/components/calendar';
import { selectSpace, fetchCalendarEvents, deleteBlockDay, selectDate, fetchOpHours } from '../app-main/actions/calendar';
import { updateExperience } from '../app-main/actions/nux';
import { Helpers } from 'shared-components/helpers/helpers';
import { NotificationTooltip } from '../elements-tooltip/notification-tooltip';
import { SyncFlow } from './shared-component/sync-flow-main';
import { OpHoursComp, reducedOpHours } from '../elements-operating-hours/operating-hours';
import { readCalendar } from '../../../api/api-calendar';

import { Spinner } from 'shared-components/decorators/spinner';
import { FormattedDate, FormattedMessage, defineMessages, injectIntl, intlShape } from 'react-intl';
import { MenuHorizontal } from '../shared-components/icons';
import {
  updateSyncStatus,
  fetchCronofyCalendars,
  fetchSyncStatusOneSpace
} from '../app-main/actions/sync';
import { SyncStatusFn } from './sync-status-fn';
import LastUpdated from 'shared-components/last-updated/LastUpdated';
import AvailabilityModalsContainer from './availability/availability-modals-container';
import { Text } from '@ps-design-system/components/Text';
import ConnectCalendarCtaV1 from './connect-calendar/components/ConnectCalendarCTA/v1';
import ConnectCalendarCtaV2 from './connect-calendar/components/ConnectCalendarCTA';
import useCalendarStore from './store/calendar/useCalendarStore';
import SyncPollingHandler from './connect-calendar/components/SyncPollingHandler';
import { StatsigContext } from '../elements-statsig-provider/statsig-context';
import { processMonthEventsToModifiers, modifiersDefaultValues } from './util/calendar-util';
import CalendarListingStatus from './calendar-main-page/CalendarListingStatus';

// HOW IT WORKS:
// Calendar is defaulted to the most recent created space by the user
//
// On load of page the current day is selected by default and chain of calls are made:
// 1) Calendar api is called to get a collection of month's events
// 2) If the current day has an event, call bookings bundle api to populate info on the right pane
// 3) If the current day does not have an event, render 'Space is available' placeholder;
//
// If user selects another space:
// 1) The spaceId is added to url params for consistent page reload
// 2) The same chain of calls are made when user loads the pages
// 3) We persist the date and month on the calendar where the user was at in the previous space
//
// If user selects a date:
// 1) Change date and day of week on the right pane header
// 2) If user selects a day w/o an event, reset bookings data in appState & render 'Space is available' placeholder;
// 3) If user selects a day w/ an event, call bookings bundle api to populate info on the right pane
//
// If user changes month:
// 1) Call calendar api to get new month events object
// 2) Right pane info does not change

export class CalendarMainClass extends React.Component {
  constructor (props, context) {
    super(props, context);
    this.state = {
      showModal: false,
      calendarDate: null,
      loadAllCalendarData: true,
      survey: {
        has_external_calendar: true,
        cannot_sync_reasons: '',
        reminder_frequency: '',
        reminder_preference: ''
      },
      showMobileCalendarNav: false,
      nuxAnalyticCalled: false,
      calendarEventModifiers: { ...modifiersDefaultValues },
      currentMonth: new Date()
    };
    this.updateSurveyState = this.updateSurveyState.bind(this);
    this.handleSpacesClick = this.handleSpacesClick.bind(this);
    this.deleteBlockDay = this.deleteBlockDay.bind(this);
    this.toggleBlockDayModal = this.toggleBlockDayModal.bind(this);
    this.fetchCalendar = this.fetchCalendar.bind(this);
    this.fetchCalendarCb = this.fetchCalendarCb.bind(this);
    this.updateNux = this.updateNux.bind(this);
    this.goToSettingsPage = this.goToSettingsPage.bind(this);
    this.openSyncFlowModal = this.openSyncFlowModal.bind(this);
    this.onDayClickCallback = this.onDayClickCallback.bind(this);
    this.callFetchCalendar = this.callFetchCalendar.bind(this);
    this.getTimeRangeString = this.getTimeRangeString.bind(this);
    this.disconnectSync = this.disconnectSync.bind(this);
    this.startSync = this.startSync.bind(this);
    this.refreshSync = this.refreshSync.bind(this);
    this.openCalendarSelectionModal = this.openCalendarSelectionModal.bind(this);
    this.checkSelectCal = this.checkSelectCal.bind(this);
    this.getExportId = this.getExportId.bind(this);
    this.syncCronofyCalendar = this.syncCronofyCalendar.bind(this);
    this.checkForModalsInUrl = this.checkForModalsInUrl.bind(this);
    this.clearHashUrl = this.clearHashUrl.bind(this);
    this.displayOptionsOnSyncModal = this.displayOptionsOnSyncModal.bind(this);
    this.calendarSurveyAnalytics = this.calendarSurveyAnalytics.bind(this);
    this.handleOperatingHoursLink = this.handleOperatingHoursLink.bind(this);
    this.toggleBufferTimeModal = this.toggleBufferTimeModal.bind(this);
  }

  componentDidMount () {
    const { location, calendar } = this.props;

    const params = new URLSearchParams(location.search);
    const date = params.get('date');
    this.setSelectedDate(date);

    const { hash } = location;
    const { a = '' } = Helpers.parseHash(hash);
    this.checkForModalsInUrl(a);
    const urlDate = date && moment(date, 'YYYY-MM-DD', true).isValid() ? moment(date).toDate() : null;
    const initialDate = urlDate || calendar.selectedDate || new Date();

    this.setState({ currentMonth: initialDate });

    const { selectedSpace } = calendar;
    if (selectedSpace) {
      this.fetchCalendar(initialDate, this.fetchCalendarCb);
    }
  }

  setSelectedDate = (date) => {
    const { dispatch } = this.props;

    if (date) {
      const parsedDate = moment(date, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        dispatch(selectDate(parsedDate.toDate()));
      }
    }
  }

  componentDidUpdate (prevProps) {
    const { dispatch, intl } = this.props;
    const error = _.get(this.props, ['calendar', 'error'], '');

    const nuxExperiences = _.get(this.props, ['nux', 'experiences'], []);
    const accessToken = _.get(this.props, ['auth', 'accessToken'], '');
    const prevNuxExperiences = _.get(prevProps, ['nux', 'experiences'], []);

    if (0 === prevNuxExperiences.length && nuxExperiences.length) {
      const badgeExperienceId = _.get(_.filter(nuxExperiences, { name: 'calendar-new-badge' }), ['0', '_id']);
      if (badgeExperienceId) dispatch(updateExperience(badgeExperienceId));
    }

    const selectedDate = _.get(this.props, ['calendar', 'selectedDate'], new Date());
    const prevSpace = _.get(prevProps, ['calendar', 'selectedSpace']);
    const currentSpace = _.get(this.props, ['calendar', 'selectedSpace']);

    if (prevSpace !== currentSpace) {
      if (error) return;
      const { calendarDate } = this.state;
      // this will default to first of the month if people switch spaces
      // except when the first of the month is before current date
      const date = !calendarDate ? selectedDate : moment().isAfter(calendarDate) ? new Date() : calendarDate;

      dispatch(fetchOpHours(currentSpace, accessToken, intl));
      dispatch(fetchSyncStatusOneSpace())
        .then(() => {
          this.checkSelectCal();
        });

      // Refetch calendar events, then select the day to fetch its bookings
      this.callFetchCalendar(date);
    }

    const prevBlockDayHash = _.get(prevProps, ['location', 'hash']);
    const BlockDayHash = _.get(this.props, ['location', 'hash']);
    const { a = '' } = Helpers.parseHash(BlockDayHash);

    if (prevBlockDayHash !== BlockDayHash) {
      this.checkForModalsInUrl(a);
    }

    const prevSyncFetch = _.get(prevProps, 'sync.syncIsFetching');
    const currentSyncFetch = _.get(this.props, 'sync.syncIsFetching');

    if (prevSyncFetch && !currentSyncFetch) {
      this.checkSelectCal();
    }

    const prevLoadCalendarAndDayEvents = _.get(prevProps, ['calendar', 'loadCalendarAndDayEvents']);
    const loadCalendarAndDayEvents = _.get(this.props, ['calendar', 'loadCalendarAndDayEvents']);
    if (!prevLoadCalendarAndDayEvents && loadCalendarAndDayEvents) {
      if (error) return;
      this.setState({ loadAllCalendarData: true });
    }

    const prevMonthEvents = _.get(prevProps, ['calendar', 'month_events']);
    const currentMonthEvents = _.get(this.props, ['calendar', 'month_events']);
    const prevSelectedDate = _.get(prevProps, ['calendar', 'selectedDate']);
    const currentSelectedDate = _.get(this.props, ['calendar', 'selectedDate']);

    if (!_.isEqual(prevMonthEvents, currentMonthEvents) ||
        !moment(prevSelectedDate).isSame(currentSelectedDate, 'day')) {
      const newModifiers = processMonthEventsToModifiers(currentMonthEvents);
      this.setState({ calendarEventModifiers: newModifiers });
    }
  }

  updateSurveyState (key, value) {
    const surveyData = { ...this.state.survey };
    surveyData[key] = value;
    this.setState({ survey: surveyData });
  }

  checkForModalsInUrl (whichModal) {
    switch (whichModal) {
    case 'block-day':
      this.props.toggleModal('BlockDayModal');
      break;
    case 'select-calendar':
      this.checkSelectCal();
      break;
    case 'import-calendar':
      this.props.toggleModal('ImportCalendarModal');
      break;
    case 'external-calendar':
      this.props.toggleModal('SyncFlow1');
      break;
    case 'buffer-time':
      this.props.toggleModal('BufferTimeModal');
      break;
    default:
      this.props.toggleModal('');
    }
  }

  calendarSurveyAnalytics (step_viewed) {
    const { calendar = {}, SSOid } = this.props;
    const spaceId = calendar.selectedSpace || '';
    analyticsIntegrations.calSyncSurveyViewed(spaceId, SSOid, step_viewed);
  }

  displayOptionsOnSyncModal () {
    const { survey } = this.state;
    return survey.has_external_calendar && '' === survey.cannot_sync_reasons;
  }

  addDateToUrlParams (date) {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    const params = new URLSearchParams(location.search);

    params.set('date', formattedDate);
    window.history.pushState({}, '', `${location.pathname}?${params.toString()}`);
  }

  onDayClickCallback (date) {
    const { dispatch } = this.props;
    dispatch(selectDate(date));
    this.addDateToUrlParams(date);
    this.setState({ currentMonth: date });
  }

  toggleBlockDayModal (toolipActions) {
    const { location } = this.props;
    const { hash, pathname } = location;
    const hashBlockDay = hash ? '' : '#a=block-day';
    // if user opens the modal and nux tooltip is open, close tooltip
    if (!_.isEmpty(toolipActions) && 'undefined' !== typeof toolipActions.showTooltip) {
      const { showTooltip } = toolipActions || {};
      showTooltip(false);
      this.updateNux('calendar-block-day-tooltip');
    }
    this.context.router.push({ pathname, hash: hashBlockDay });
  }

  toggleBufferTimeModal (toolipActions) {
    const { calendar = {}, SSOid = '' } = this.props;
    const { selectedSpace, spaces = [] } = calendar;
    const selectedSpaceInfo = spaces && spaces.find(space => space._id === selectedSpace);
    const metro = selectedSpaceInfo?.metro;
    const properties = {
      hostId: SSOid,
      spaceId: selectedSpace,
      deviceType: Helpers.isMobile() ? 'Mobile Web' : 'Web',
      metro
    };
    analyticsIntegrations.bufferTimeBtnClicked(properties);
    const { location } = this.props;
    const { hash, pathname } = location;
    const hashBlockDay = hash ? '' : '#a=buffer-time';
    // if user opens the modal and nux tooltip is open, close tooltip
    if (!_.isEmpty(toolipActions) && 'undefined' !== typeof toolipActions.showTooltip) {
      const { showTooltip } = toolipActions || {};
      // Replace with Buffer Time Nux tool tip
      // showTooltip(false);
      // this.updateNux('calendar-block-day-tooltip');
    }
    this.context.router.push({ pathname, hash: hashBlockDay });
  }

  clearHashUrl () {
    const { location } = this.props;
    const { hash, pathname } = location;

    if (hash) {
      this.context.router.push({ pathname, hash: '' });
    }
  }

  updateNux (name) {
    const { dispatch } = this.props;
    const nuxExperiences = _.get(this.props, ['nux', 'experiences'], []);
    const tooltipExperienceId = _.get(_.filter(nuxExperiences, { name }), ['0', '_id']);
    if (tooltipExperienceId) dispatch(updateExperience(tooltipExperienceId));
  }

  goToSettingsPage () {
    const { calendar = {} } = this.props;
    const { selectedSpace = '' } = calendar;
    this.updateNux('calendar-sync-tooltip');
    window.location.href = `/calendar/${selectedSpace}/settings`;
    // return this.context.router.push({ pathname: `/calendar/${selectedSpace}/settings` });
  }

  openSyncFlowModal () {
    const { calendar = {}, profile = {} } = this.props;
    const { selectedSpace = '' } = calendar;
    const { SSOid = '' } = profile;
    this.updateNux('calendar-sync-tooltip');
    // Do we need to update analyics here?
    analyticsIntegrations.calendarSync('Calendar sync modal viewed', selectedSpace, SSOid, 'calendar page');
    return this.props.toggleModal('ConnectExternalCalendar');
  }

  handleSpacesClick (spaceId, closeFn) {
    const { dispatch } = this.props;
    this.props.calendarStore.setSelectedSpaceId(spaceId);
    dispatch(selectSpace(spaceId));
    this.context.router.push(`/calendar/${spaceId}`);
    return closeFn();
  }

  syncCronofyCalendar (calendarName) {
    const { dispatch, toggleModal } = this.props;
    // this callback is passed to fetchSyncStatusOneSpace to get ahold of the calendarId
    const cb = (calendars = []) => {
      const selectedCalendar = calendars.find(cal => cal.calendar_name === calendarName);
      const selectedSpace = this.props?.sync?.calendarData?.space_id || '';
      const path = `${selectedSpace}/calendar/${selectedCalendar.calendar_id}/connect`;
      return dispatch(updateSyncStatus(path))
        .then(()=>{
          const { SSOid, calendar } = this.props;
          analyticsIntegrations.calendarSync('Calendar sync true', calendar.selectedSpace, SSOid, 'calendar page', 'ical');
          toggleModal('SuccessIcalModal');
        });
    };
    dispatch(fetchSyncStatusOneSpace())
      .then(() => dispatch(fetchCronofyCalendars(cb)))
      .catch(() => {});
  }

  checkSelectCal () {
    const { sync = {}, location = {}, dispatch } = this.props;
    const { pathname = '', hash = {} } = location;
    const { a = '' } = Helpers.parseHash(hash);

    if (false === this.props.useSyncFlowV2 && 'select-calendar' === a && '' !== sync.status) {
      if ('AUTHORIZED' === sync.status) {
        this.props.toggleModal('SyncSelectCal');
        return dispatch(fetchCronofyCalendars());
      }
      return this.context.router.push({ pathname });
    }
    return null;
  }

  fetchCalendar (date, cb) {
    // date = new Date()
    // cb = fetchCalendarCb
    const { dispatch, calendar = {}, intl } = this.props;
    const { selectedSpace = '' } = calendar;
    const accessToken = _.get(this.props, ['auth', 'accessToken'], '');
    this.setState({ calendarDate: date });
    return dispatch(fetchCalendarEvents(selectedSpace, date, accessToken, cb, intl));
  }

  fetchCalendarCb () {
    return this.setState({ loadAllCalendarData: false });
  }

  onMonthChange = (newMonthDate) => {
    this.setState({ currentMonth: newMonthDate });
    this.fetchCalendar(newMonthDate, () => {
      this.setState({ loadAllCalendarData: false });
    });
  }

  // Refetch the month's events and re-select the given date so the right pane
  // (day_events) reflects freshly created/removed blocks immediately, even when
  // the block is in the currently displayed month. Also navigates the day picker
  // to the block's month.
  callFetchCalendar (date) {
    const { dispatch } = this.props;
    const targetDate = date || new Date();
    this.setState({ currentMonth: targetDate });
    return this.fetchCalendar(targetDate).then(() => {
      dispatch(selectDate(targetDate));
    });
  }

  deleteBlockDay (groupId, bufferTime = false) {
    const { dispatch, calendar = {}, intl } = this.props;
    const { selectedDate = '' } = calendar;
    // Delete block day calls
    // After successfull block day call we make another call to update calendar
    dispatch(deleteBlockDay(groupId, intl))
      .then(() => {
        this.callFetchCalendar(selectedDate);
        if (bufferTime) {
          const { calendar = {}, SSOid = '' } = this.props;
          const { selectedSpace, spaces = [] } = calendar;
          const selectedSpaceInfo = spaces.find(space => space._id === selectedSpace);
          const metro = selectedSpaceInfo?.metro;
          const properties = {
            hostId: SSOid,
            spaceId: selectedSpace,
            deviceType: Helpers.isMobile() ? 'Mobile Web' : 'Web',
            metro
          };
          analyticsIntegrations.bufferTimeDeleteBtnClicked(properties);
        }
      })
      .catch(() => {});
  }

  getTimeRangeString (hours = []) {
    const start = _.get(hours, 'start.time', {});
    const end = _.get(hours, 'end.time', {});

    const toLocalizedString = (time) => {
      return this.props.intl.formatTime(moment(`${time.h || 0}:${time.m || 0}`, 'HH:mm').format());
    };

    return `${toLocalizedString(start)} - ${toLocalizedString(end)}`;
  }

  disconnectSync () {
    this.props.toggleModal('DisconnectSyncModal');
  }

  handleOperatingHoursLink () {
    if (!analyticsIntegrations) return;
    const { profile } = this.props;
    const { SSOid } = profile;
    const properties = {
      source: 'nav',
      hostId: SSOid,
      deviceType: Helpers.isMobile() ? 'Mobile Web' : 'Web'
    };
    analyticsIntegrations.operatingHoursLinkClicked(properties);
  }

  startSync () {
    const { calendar = {}, profile = {} } = this.props;
    const { selectedSpace = '' } = calendar;
    const { SSOid } = profile;
    this.props.toggleModal('SyncFlow1');
    return analyticsIntegrations.calendarSync('Calendar sync modal viewed', selectedSpace, SSOid, 'calendar settings page');
  }

  refreshSync () {
    const { dispatch, calendar = {} } = this.props;
    const { selectedSpace = '' } = calendar;
    const path = `${selectedSpace}/calendar/resync`;
    dispatch(updateSyncStatus(path));
  }

  openCalendarSelectionModal () {
    // opens calendar modal
    const { location = {} } = this.props;
    const { pathname = '' } = location;

    this.context.router.push({ pathname, hash: '#a=select-calendar' });
  }

  getExportId () {
    const { calendar = {} } = this.props;
    const { selectedSpace = '' } = calendar;
    const path = `exports/space/${selectedSpace}/ics/export_id`;
    const accessToken = _.get(this.props, ['auth', 'accessToken'], '');

    readCalendar(path, accessToken, ({ export_id }) => {
      this.props.toggleModal('ShareCalendar', { export_id });
    });
  }

  render () {
    const { showModal, showMobileCalendarNav, calendarEventModifiers, currentMonth } = this.state;
    const { calendar = {}, nux = {}, sync = {}, intl } = this.props;
    const {
      selectedSpace = '',
      selectedDate = '',
      error = '',
      day_events = {},
      operating_hours: opHours,
      spaceById = {},
      lastUpdatedDate
    } = calendar;
    const { isFetching: isDayEventsFetching, events: dayEvents = [] } = day_events;
    const spaceObject = spaceById[selectedSpace] || {};

    const translationMessages = defineMessages({
      notificationToolTipTitle: {
        id: 'hosting.calendar_main.notification_tooltip_title',
        defaultMessage: 'Sync Calendar'
      },
      opHoursImageAlt: {
        id: 'hosting.calendar_main.op_hours_hint_image_alt',
        defaultMessage: 'lightbulb'
      },
      inquiry: {
        id: 'hosting.calendar_main.inquiry',
        defaultMessage: 'Inquiry'
      },
      request: {
        id: 'hosting.calendar_main.request',
        defaultMessage: 'Request'
      },
      offer: {
        id: 'hosting.calendar_main.offer',
        defaultMessage: 'Offer'
      },
      booking: {
        id: 'hosting.calendar_main.booking',
        defaultMessage: 'Booking'
      }
    });
    const legends = [
      { title: translationMessages.inquiry, key: 'dot-divider text-blue' },
      { title: translationMessages.request, key: 'dot-divider text-yellow' },
      { title: translationMessages.offer, key: 'dot-divider text-wine' },
      { title: translationMessages.booking, key: 'green-triangle' }
    ];
    const syncErrors = !!sync.syncErrorSpaceIds.length;
    const nuxExperiences = _.get(nux, ['experiences'], []);
    const showBlockDayExperience = _.some(nuxExperiences, { name: 'calendar-block-day-tooltip' });
    const showBufferTimeExperience = _.some(nuxExperiences, { name: 'calendar-buffer-time-tooltip ' });
    if (showBufferTimeExperience && analyticsIntegrations && !this.state.nuxAnalyticCalled) {
      const properties = {
        deviceType: Helpers.isMobile() ? 'Mobile Web' : 'Web',
        hostId: this.props.SSOid
      };
      analyticsIntegrations.bufferTimeNuxViewed(properties);
      this.setState({ nuxAnalyticCalled: true });
    }
    const showCalExportExperience = _.some(nuxExperiences, { name: 'calendar-export-tooltip' });
    const showSyncExperience = _.some(nuxExperiences, { name: 'calendar-sync-tooltip' });
    // Op Hours variable block
    const dow = moment(selectedDate).isoWeekday();
    const getDayOpHours = _.get(reducedOpHours(opHours, intl), [dow, 'operating_hours'], []);
    const isOpHoursClosed = !_.some(opHours, { dow });
    const hasOpHours = spaceObject.has_ophours;
    const country = _.get(spaceObject, 'country', '');

    const spaces = this.props.calendarStore?.spaces ?? [];
    const selectedSpaceId = this.props.calendarStore?.selectedSpaceId;
    const formattedSelectedDate = selectedDate ? moment(selectedDate).toDate() : new Date();
    const monthToDisplay = currentMonth ? moment(currentMonth).toDate() : new Date();

    const dayPickerModifiers = {
      ...calendarEventModifiers,
      visuallyDisabled: [...calendarEventModifiers?.disabled, ...calendarEventModifiers?.fullyBooked],
      disabled: [],
      fullyBooked: []
    };

    const newModifiersClassNames = {
      visuallyDisabled: '!tw-text-gray-500 tw-line-through'
    };

    const listingsBySpaceId = this.props.calendarStore?.listingsBySpaceId ?? {};
    const selectedListings = listingsBySpaceId[selectedSpace] ?? [];
    const filteredListings = selectedListings.filter((listing) => listing?.status === 'PAUSED' || listing?.status === 'PAUSED_INTERNALLY');

    return (
      <div>
        <div
          id="calendar-nav"
          className="calendar-nav-web flex space-between middle"
        >
          <SpaceSelector
            selectedSpaceId={selectedSpaceId}
            spacesClickFn={this.handleSpacesClick}
            spaces={_.keyBy(spaces, '_id')}
            containerClassName='mobile-full-width'
            darkMode
          />
          <div className="calendar-nav-menu ">
            <a
              className="btn btn-link text-white"
              href={`/your-space/${selectedSpace}/availability`}
              onClick={this.handleOperatingHoursLink}
            >
              <FormattedMessage
                id="hosting.calendar_main.op_hours"
                defaultMessage="Bookable Hours"
              />
            </a>
            <NotificationTooltip
              id="notification_tooltip_buffer_time_nux"
              show={showBufferTimeExperience}
              anchor={
                <button className="btn btn-default margin-lg-right">
                  <FormattedMessage
                    id="hosting.calendar_main.notification_tooltip_buffer_time_button_text"
                    defaultMessage="Buffer Time"
                  />
                </button>
              }
              anchorClickCB={this.toggleBufferTimeModal}
              exitCB={() => {
                this.updateNux('calendar-buffer-time-tooltip ');
              }}
              title="Buffer Time"
              arrowPlacement={{ arrowLeft: 200 }}
            >
              <FormattedMessage
                id="hosting.calendar_main.buffer_time_calendar_nux_copy"
                defaultMessage="Prepare for your next guest by automatically blocking out time between each booking."
              />
            </NotificationTooltip>
            <NotificationTooltip
              id="notification_tooltip_block_time_nux"
              show={showBlockDayExperience}
              anchor={<button className="btn btn-default">
                <FormattedMessage
                  id="hosting.calendar_main.notification_tooltip_block_time_button_text"
                  defaultMessage="Block Time"
                />
              </button>}
              anchorClickCB={this.toggleBlockDayModal}
              exitCB={() => { this.updateNux('calendar-block-day-tooltip'); }}
              title="Block time"
              arrowPlacement={{ arrowLeft: 225 }}
            >
              <FormattedMessage
                id="hosting.calendar_main.notification_tooltip_block_time_text"
                defaultMessage="Block time on your calendar to keep your availability up to date."
              />
            </NotificationTooltip>
          </div>
          <div className="mobile-calendar-nav-menu ">
            <a
              className="btn btn-secondary"
              onClick={()=>this.setState({ showMobileCalendarNav: !showMobileCalendarNav })}
            >
              <div style={{ fontSize: '24px' }}><MenuHorizontal /></div>
            </a>
            {showMobileCalendarNav && <ul className="mobile-calendar-nav-submenu ">
              <li className="mobile-nav-menu-item">
                <a
                  rel="nofollow"
                  style={{ color: 'black' }}
                  onClick={()=> this.setState({ showMobileCalendarNav: false }, this.toggleBlockDayModal())}
                >
                  <FormattedMessage
                    id="hosting.calendar_main.notification_tooltip_block_time_button_text"
                    defaultMessage="Block Time"
                  />
                </a>
              </li>
              <li className="mobile-nav-menu-item">
                <a
                  rel="nofollow"
                  style={{ color: 'black' }}
                  onClick={()=> this.setState({ showMobileCalendarNav: false }, this.toggleBufferTimeModal())}
                >
                  <FormattedMessage
                    id="hosting.calendar_main.notification_tooltip_buffer_time_button_text"
                    defaultMessage="Buffer Time"
                  />
                </a>
              </li>
              <li className="mobile-nav-menu-item">
                <a
                  rel="nofollow"
                  style={{ color: 'black' }}
                  href={`/your-space/${selectedSpace}/availability`}
                  onClick={this.handleOperatingHoursLink}
                >
                  <FormattedMessage
                    key='0'
                    id="hosting.calendar_main.edit_operating_hours"
                    defaultMessage="Edit bookable hours"
                  />
                </a>
              </li>
            </ul>}
          </div>
        </div>
        <div className="container-fluid calendar-container-web ">
          <div className="row calendar-row ">
            <div className="calendar-main ">
              <div className="calendar-left-col  calendar-right-border ">
                <div className="calendar-left-col-container ">
                  <Calendar
                    selected={formattedSelectedDate}
                    onSelect={this.onDayClickCallback}
                    month={monthToDisplay}
                    mode="single"
                    locale={intl.locale}
                    numberOfMonths={1}
                    showLoading={false}
                    fullWidth={false}
                    components={{
                      DayContent: CalendarAvailabilityDayContent
                    }}
                    modifiers={dayPickerModifiers}
                    modifiersClassNames={newModifiersClassNames}
                    onMonthChange={this.onMonthChange}
                  />
                  <div className="calendar-legend">
                    <CalendarEventLegend/>
                  </div>
                  {
                    lastUpdatedDate &&
                    <div className="row last-updated-container text-left">
                      <div className="col-xs-12">
                        <span className="last-updated-message"><LastUpdated lastUpdatedDate={lastUpdatedDate}/></span>
                      </div>
                    </div>
                  }
                  { this.props.useSyncFlowV2 ?
                    <ConnectCalendarCtaV2 /> :
                    <ConnectCalendarCtaV1
                      sync={sync}
                      openSyncFlowModal={this.openSyncFlowModal}
                      getEllipsesDropdownItems={() => SyncStatusFn(sync, this.disconnectSync, this.startSync, this.refreshSync, this.openCalendarSelectionModal, this.props.intl)}
                    />
                  }
                  {
                    0 !== opHours.length &&
                    <div className="margin-xxl-top panel panel-body op-hours-container">
                      <Text typeface="body-2-m">
                        <FormattedMessage
                          id="hosting.calendar_main.op_hours"
                          defaultMessage="Bookable Hours"
                        />
                      </Text>
                      <a
                        style={{ padding: 0 }}
                        href={`/your-space/${selectedSpace}/availability`}
                      >
                        <Text typeface="body-2-m" className="pull-right underline">
                          <FormattedMessage
                            id="hosting.calendar_main.op_hours_edit_link"
                            defaultMessage="Edit"
                          />
                        </Text>
                      </a>
                      {
                        !hasOpHours &&
                        <div className="margin-lg-top">
                          <Text typeface="body-2">
                            <FormattedMessage
                              id="hosting.calendar_main.op_hours_edit_text"
                              defaultMessage="Set your bookable hours using the edit button."
                            />
                          </Text>
                        </div>
                      }
                      <OpHoursComp opHours={opHours} containerClass="text-base" />
                    </div>
                  }
                </div>
              </div>
              <div className="flex-one calendar-right-col  padding-xl-top">
                <div className="calendar-right-col-container ">
                  {
                    error ?
                      <div style={{ marginTop: '40px' }} className="text-red">{error}</div> :
                      <div>
                        <ul className="list-inline calendar-header-container  flex end flex-wrap">
                          <li className="v-center flex-one">
                            <h3 className="date-header">
                              <FormattedDate
                                value={ selectedDate }
                                month="short"
                                day="numeric"
                                weekday="long"
                              />
                            </h3>
                            <div className="text-gray-600">
                              <span>
                                <FormattedMessage
                                  id="hosting.calendar_main.op_hours"
                                  defaultMessage="Bookable Hours"
                                />: </span>
                              <div className="op-hours-title-container">
                                {
                                  _.map(getDayOpHours, (value, i) => {
                                    return <span key={i} className="op-hours-title">{this.getTimeRangeString(value)}{i === (getDayOpHours.length - 1) ? '' : ',\u00A0'}</span>;
                                  })
                                }
                              </div>
                            </div>
                          </li>
                          {/* TEMPORARILY DISABLING EXPORT BUTTON as a precaution for current mutual sync issue on BE */}
                          {/* <li className="v-center header-btn-container">
                          <NotificationTooltip
                            anchor={
                              <button className="btn btn-default">
                                <FormattedMessage
                                  id="hosting.calendar_main.export_calendar_button"
                                  defaultMessage="Export Calendar"
                                />
                              </button>
                            }
                            show={showCalExportExperience}
                            exitCB={() => { this.updateNux('calendar-export-tooltip'); }}
                            anchorClickCB={()=>this.getExportId()}
                            title={
                              <FormattedMessage
                                id="hosting.calendar_main.export_calendar_sync_title"
                                defaultMessage="Export Calendar"
                              />
                            }
                            arrowPlacement={{ arrowLeft: 200 }}
                          >
                            <FormattedMessage
                              id="hosting.calendar_main.export_calendar_description"
                              defaultMessage="Export your Peerspace calendar to your calendars on other sites."
                            />
                          </NotificationTooltip>
                        </li> */}
                        </ul>
                        <hr />
                        {
                          filteredListings.map((listing) => {
                            return (
                              <CalendarListingStatus key={listing?._id} id={listing?._id} status={listing?.status} useType={listing?.use_type} />
                            );
                          })
                        }
                        {
                          isDayEventsFetching &&
                          <div>
                            <Spinner additionalStyle="gray" />
                          </div>
                        }
                        {
                          !isDayEventsFetching && dayEvents.length ?
                            dayEvents.map((event) => {
                              if (event) {
                                return (
                                  'space' === event.type || 'cronofy' === event.type || 'buffer' === event.type ?
                                    <BlockDayComp
                                      blockDayEvent={event}
                                      deleteBlockDayFn={this.deleteBlockDay}
                                    /> :
                                    <BookingInfoComp
                                      booking={event}
                                      selectedDate={selectedDate}
                                    />
                                );
                              }
                              return null;
                            }) : null
                        }
                        {
                          !dayEvents.length && !isOpHoursClosed ? <SpaceIsAvailableComp /> : null
                        }
                      </div>
                  }
                </div>
              </div>
            </div>
          </div>
          <SyncFlow
            spaceId={selectedSpace}
            deviceType={Helpers.isMobile() ? 'Mobile Web' : 'Web'}
            selectedSpace={spaceObject?.title || ''}
            spaceObject={spaceObject}
            changeModal={this.props.toggleModal}
            syncCronofyCalendar={this.syncCronofyCalendar}
            getExportId={this.getExportId}
            country={country}
            toggleBlockDayModal={this.toggleBlockDayModal}
            callFetchCalendar={this.callFetchCalendar}
            clearHashUrl={this.clearHashUrl}
            updateSurveyState={this.updateSurveyState}
            survey={this.state.survey}
            displayOptionsOnSyncModal={this.displayOptionsOnSyncModal()}
            {...this.props}
            calendarSurveyAnalytics={this.calendarSurveyAnalytics}
          />
        </div>
        <AvailabilityModalsContainer />
      </div>
    );
  }
}

CalendarMainClass.contextTypes = {
  router: PropTypes.object
};

CalendarMainClass.propTypes = {
  calendar: PropTypes.object,
  profile: PropTypes.object,
  nux: PropTypes.object,
  location: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
  importICSCalendar: PropTypes.func,
  calendarStore: PropTypes.object,
  useSyncFlowV2: PropTypes.bool,
  sync: PropTypes.object,
  intl: intlShape
};

const CalendarMainBase = (props) => {
  /**
   * Zustand calendar store (migration in progress)
   */
  const calendarStore = useCalendarStore();
  const { featureGates } = useContext(StatsigContext);

  return (
    <>
      <SyncPollingHandler/>
      <CalendarMainClass
        {...props}
        useSyncFlowV2={featureGates.sync_flow_v2}
        calendarStore={calendarStore}
      />
    </>
  );
};

export const CalendarMain = injectIntl(CalendarMainBase);
